import React, { useState, useEffect } from 'react';
import { submitScripApplication } from '../api/scrip.api';
import './ScripRegister.css';

const STEPS = [
  { num: 1, label: 'Identification',     short: 'Identity'    },
  { num: 2, label: 'Legal & Residency',  short: 'Legal'       },
  { num: 3, label: 'Employment History', short: 'Employment'  },
  { num: 4, label: 'Education',          short: 'Education'   },
  { num: 5, label: 'Review & Submit',    short: 'Review'      },
];

function Field({ label, required, error, hint, children }) {
  return (
    <div className={`sr-field${error ? ' sr-field--error' : ''}`}>
      <label className="sr-label">
        {label}{required && <span className="sr-required">*</span>}
      </label>
      {children}
      {hint  && !error && <span className="sr-hint">{hint}</span>}
      {error && <span className="sr-error-msg">⚠ {error}</span>}
    </div>
  );
}
function Input({ error, ...props }) {
  return <input className={`sr-input${error ? ' sr-input--err' : ''}`} {...props} />;
}
function Select({ error, children, ...props }) {
  return (
    <select className={`sr-input sr-select${error ? ' sr-input--err' : ''}`} {...props}>
      {children}
    </select>
  );
}
function Textarea({ ...props }) {
  return <textarea className="sr-input sr-textarea" {...props} />;
}
function Row({ children, cols = 2 }) {
  return <div className={`sr-row sr-row--${cols}`}>{children}</div>;
}
function RadioGroup({ name, options, value, onChange }) {
  return (
    <div className="sr-radio-group">
      {options.map((opt) => (
        <label className="sr-radio" key={opt}>
          <input type="radio" name={name} value={opt} checked={value === opt} onChange={onChange} />
          <span className="sr-radio-mark" />
          {opt}
        </label>
      ))}
    </div>
  );
}

function StepIdentification({ data, setData, errors }) {
  const set = (k) => (e) => setData({ ...data, [k]: e.target.value });
  return (
    <div className="sr-step-body">
      <div className="sr-step-intro">
        <p>Provide accurate personal identification details. All information is handled with strict confidentiality per our privacy policy.</p>
      </div>
      <Row>
        <Field label="First Name" required error={errors.firstName}>
          <Input type="text" placeholder="Jane" value={data.firstName || ''} onChange={set('firstName')} error={errors.firstName} />
        </Field>
        <Field label="Last Name" required error={errors.lastName}>
          <Input type="text" placeholder="Smith" value={data.lastName || ''} onChange={set('lastName')} error={errors.lastName} />
        </Field>
      </Row>
      <Row>
        <Field label="Date of Birth" required error={errors.dob}>
          <Input type="date" value={data.dob || ''} onChange={set('dob')} error={errors.dob} />
        </Field>
        <Field label="Gender" required error={errors.gender}>
          <Select value={data.gender || ''} onChange={set('gender')} error={errors.gender}>
            <option value="">Select…</option>
            <option>Male</option><option>Female</option>
            <option>Non-binary</option><option>Prefer not to say</option>
          </Select>
        </Field>
      </Row>
      <Row>
        <Field label="Nationality" required error={errors.nationality}>
          <Input type="text" placeholder="e.g. Cameroonian" value={data.nationality || ''} onChange={set('nationality')} error={errors.nationality} />
        </Field>
        <Field label="National ID / Passport Number" required error={errors.idNumber}>
          <Input type="text" placeholder="ID / Passport No." value={data.idNumber || ''} onChange={set('idNumber')} error={errors.idNumber} />
        </Field>
      </Row>
      <Row>
        <Field label="Email Address" required error={errors.email}>
          <Input type="email" placeholder="jane@example.com" value={data.email || ''} onChange={set('email')} error={errors.email} />
        </Field>
        <Field label="Phone Number" required error={errors.phone}>
          <Input type="tel" placeholder="+237 6XX XXX XXX" value={data.phone || ''} onChange={set('phone')} error={errors.phone} />
        </Field>
      </Row>
      <Field label="Passport-Style Photo" required error={errors.photo}
        hint="Clear frontal photo, plain background — JPG/PNG, max 5 MB">
        <div className="sr-file-wrap">
          <label className="sr-file-btn">
            <span>📎 Choose File</span>
            <input type="file" accept="image/jpeg,image/png,image/webp"
              onChange={(e) => setData({ ...data, photo: e.target.files[0] || null, photoName: e.target.files[0]?.name || '' })}
              style={{ display: 'none' }} />
          </label>
          <span className="sr-file-name">{data.photoName || 'No file chosen'}</span>
        </div>
      </Field>
      <Field label="SCRIP Programme Track" required error={errors.programArea}>
        <Select value={data.programArea || ''} onChange={set('programArea')} error={errors.programArea}>
          <option value="">Select track…</option>
          <option>Finance</option><option>Security</option>
          <option>IT</option><option>Health</option><option>Logistics</option>
        </Select>
      </Field>
    </div>
  );
}

function StepLegal({ data, setData, errors }) {
  const set = (k) => (e) => setData({ ...data, [k]: e.target.value });
  return (
    <div className="sr-step-body">
      <div className="sr-step-intro">
        <p>Used solely for due diligence screening. All responses are treated as strictly confidential and evaluated objectively.</p>
      </div>
      <div className="sr-section-title">Criminal &amp; Legal Matters</div>
      <Field label="Have you faced any criminal charges in the last 6 years?" required error={errors.criminalRecord}>
        <RadioGroup name="criminalRecord" options={['No', 'Yes']} value={data.criminalRecord || ''} onChange={set('criminalRecord')} />
      </Field>
      {data.criminalRecord === 'Yes' && (
        <Field label="Please provide brief details" required error={errors.criminalDetails}
          hint="Nature of charge, outcome, and approximate date">
          <Textarea rows={3} placeholder="Nature of charge, outcome, date…"
            value={data.criminalDetails || ''} onChange={set('criminalDetails')} />
        </Field>
      )}
      <div className="sr-section-title" style={{ marginTop: '2rem' }}>Current Residence</div>
      <Row>
        <Field label="Country" required error={errors.country}>
          <Input type="text" placeholder="e.g. Cameroon" value={data.country || ''} onChange={set('country')} error={errors.country} />
        </Field>
        <Field label="Region / State / Province" required error={errors.region}>
          <Input type="text" placeholder="e.g. Littoral" value={data.region || ''} onChange={set('region')} error={errors.region} />
        </Field>
      </Row>
      <Row>
        <Field label="City / Town" required error={errors.city}>
          <Input type="text" placeholder="e.g. Douala" value={data.city || ''} onChange={set('city')} error={errors.city} />
        </Field>
        <Field label="Quarter / Neighbourhood" error={errors.quarter}>
          <Input type="text" placeholder="e.g. Akwa" value={data.quarter || ''} onChange={set('quarter')} />
        </Field>
      </Row>
      <Field label="Street Address / House Number" required error={errors.address}>
        <Input type="text" placeholder="123 Avenue de la Liberté" value={data.address || ''} onChange={set('address')} error={errors.address} />
      </Field>
      <Field label="Are you an Internally Displaced Person (IDP)?" required error={errors.isIdp}>
        <RadioGroup name="isIdp" options={['No', 'Yes']} value={data.isIdp || ''} onChange={set('isIdp')} />
      </Field>
      {data.isIdp === 'Yes' && (
        <Field label="IDP Since (approximate month/year)" required error={errors.idpSince}>
          <Input type="month" value={data.idpSince || ''} onChange={set('idpSince')} error={errors.idpSince} />
        </Field>
      )}
    </div>
  );
}

function StepEmployment({ data, setData }) {
  const jobs = data.jobs || [{ company: '', role: '', years: '' }];
  const updateJob = (i, k, v) =>
    setData({ ...data, jobs: jobs.map((j, idx) => idx === i ? { ...j, [k]: v } : j) });
  const addJob    = () => setData({ ...data, jobs: [...jobs, { company: '', role: '', years: '' }] });
  const removeJob = (i) => setData({ ...data, jobs: jobs.filter((_, idx) => idx !== i) });
  const minDate   = new Date(Date.now() + 21 * 86400000).toISOString().split('T')[0];

  return (
    <div className="sr-step-body">
      <div className="sr-step-intro">
        <p>List your employment history, starting with the most recent. Include all relevant roles.</p>
      </div>
      {jobs.map((job, i) => (
        <div className="sr-repeater-block" key={i}>
          <div className="sr-repeater-header">
            <span className="sr-repeater-label">Position {i + 1}</span>
            {jobs.length > 1 && (
              <button type="button" className="sr-remove-btn" onClick={() => removeJob(i)}>✕ Remove</button>
            )}
          </div>
          <Row cols={3}>
            <Field label="Company / Organisation">
              <Input type="text" placeholder="Company name" value={job.company} onChange={(e) => updateJob(i, 'company', e.target.value)} />
            </Field>
            <Field label="Role / Title">
              <Input type="text" placeholder="e.g. Security Analyst" value={job.role} onChange={(e) => updateJob(i, 'role', e.target.value)} />
            </Field>
            <Field label="Years Worked">
              <Input type="text" placeholder="e.g. 2019–2022" value={job.years} onChange={(e) => updateJob(i, 'years', e.target.value)} />
            </Field>
          </Row>
        </div>
      ))}
      <button type="button" className="sr-add-btn" onClick={addJob}>+ Add Another Position</button>
      <div className="sr-section-title" style={{ marginTop: '2rem' }}>Programme Enrolment Status</div>
      <Field label="Are you currently enrolled full-time in an accredited intelligence programme?">
        <RadioGroup name="enrolled" options={['Yes', 'No', 'Graduating soon']}
          value={data.enrolled || ''} onChange={(e) => setData({ ...data, enrolled: e.target.value })} />
      </Field>
      <Field label="Desired Programme Start Date" hint="Must be at least 3 weeks from today">
        <Input type="date" min={minDate} value={data.startDate || ''}
          onChange={(e) => setData({ ...data, startDate: e.target.value })} />
      </Field>
    </div>
  );
}

function StepEducation({ data, setData }) {
  const certs = data.certifications || [{ name: '', institution: '', year: '' }];
  const set   = (k) => (e) => setData({ ...data, [k]: e.target.value });
  const updateCert = (i, k, v) =>
    setData({ ...data, certifications: certs.map((c, idx) => idx === i ? { ...c, [k]: v } : c) });
  const addCert    = () => setData({ ...data, certifications: [...certs, { name: '', institution: '', year: '' }] });
  const removeCert = (i) => setData({ ...data, certifications: certs.filter((_, idx) => idx !== i) });

  return (
    <div className="sr-step-body">
      <div className="sr-step-intro">
        <p>Provide your two most recent educational qualifications and any relevant professional certifications.</p>
      </div>
      {[1, 2].map((level) => (
        <div className="sr-repeater-block" key={level}>
          <div className="sr-repeater-header">
            <span className="sr-repeater-label">{level === 1 ? 'Most Recent Qualification' : 'Previous Qualification'}</span>
          </div>
          <Row>
            <Field label="Certificate / Degree Name">
              <Input type="text" placeholder="e.g. BSc Intelligence Studies"
                value={data[`edu${level}Cert`] || ''} onChange={set(`edu${level}Cert`)} />
            </Field>
            <Field label="Institution">
              <Input type="text" placeholder="University or College"
                value={data[`edu${level}Institution`] || ''} onChange={set(`edu${level}Institution`)} />
            </Field>
          </Row>
          <Row>
            <Field label="Year of Completion">
              <Input type="number" placeholder="e.g. 2022" min="1970" max={new Date().getFullYear() + 5}
                value={data[`edu${level}Year`] || ''} onChange={set(`edu${level}Year`)} />
            </Field>
            <Field label="Level of Study">
              <Select value={data[`edu${level}Level`] || ''} onChange={set(`edu${level}Level`)}>
                <option value="">Select…</option>
                <option>Secondary / High School</option><option>Diploma / HND</option>
                <option>Bachelor's Degree</option><option>Master's Degree</option>
                <option>Doctorate / PhD</option><option>Professional Certificate</option>
              </Select>
            </Field>
          </Row>
        </div>
      ))}
      <div className="sr-section-title" style={{ marginTop: '2rem' }}>Professional Certifications</div>
      <p className="sr-section-note">Industry certifications e.g. CISSP, CPP, CFE, PMP — leave blank if none.</p>
      {certs.map((cert, i) => (
        <div className="sr-repeater-block" key={i}>
          <div className="sr-repeater-header">
            <span className="sr-repeater-label">Certification {i + 1}</span>
            {certs.length > 1 && (
              <button type="button" className="sr-remove-btn" onClick={() => removeCert(i)}>✕ Remove</button>
            )}
          </div>
          <Row cols={3}>
            <Field label="Certification Name">
              <Input type="text" placeholder="e.g. CISSP" value={cert.name} onChange={(e) => updateCert(i, 'name', e.target.value)} />
            </Field>
            <Field label="Issuing Body">
              <Input type="text" placeholder="e.g. (ISC)²" value={cert.institution} onChange={(e) => updateCert(i, 'institution', e.target.value)} />
            </Field>
            <Field label="Year Issued">
              <Input type="number" placeholder="2023" min="1990" max={new Date().getFullYear()} value={cert.year} onChange={(e) => updateCert(i, 'year', e.target.value)} />
            </Field>
          </Row>
        </div>
      ))}
      <button type="button" className="sr-add-btn" onClick={addCert}>+ Add Certification</button>
    </div>
  );
}

function StepReview({ formData, consentChecked, setConsentChecked }) {
  const { identity, legal, employment, education } = formData;
  const ReviewSection = ({ title, rows }) => (
    <div className="sr-review-section">
      <div className="sr-review-section-title">{title}</div>
      <div className="sr-review-rows">
        {rows.filter(r => r.value).map(({ label, value }) => (
          <div className="sr-review-row" key={label}>
            <span className="sr-review-label">{label}</span>
            <span className="sr-review-value">{value}</span>
          </div>
        ))}
      </div>
    </div>
  );
  return (
    <div className="sr-step-body">
      <div className="sr-step-intro">
        <p>Review all information carefully before submitting. Upon successful submission you will receive a unique reference code — save it to track your application.</p>
      </div>
      <ReviewSection title="01 — Identification" rows={[
        { label: 'Full Name',       value: `${identity.firstName || ''} ${identity.lastName || ''}`.trim() },
        { label: 'Date of Birth',   value: identity.dob },
        { label: 'Gender',          value: identity.gender },
        { label: 'Nationality',     value: identity.nationality },
        { label: 'ID / Passport',   value: identity.idNumber },
        { label: 'Email',           value: identity.email },
        { label: 'Phone',           value: identity.phone },
        { label: 'Programme Track', value: identity.programArea },
        { label: 'Photo',           value: identity.photoName || identity.photo?.name },
      ]} />
      <ReviewSection title="02 — Legal & Residency" rows={[
        { label: 'Criminal Record (6yr)', value: legal.criminalRecord },
        { label: 'Criminal Details',      value: legal.criminalDetails },
        { label: 'Country',               value: legal.country },
        { label: 'Region',                value: legal.region },
        { label: 'City',                  value: legal.city },
        { label: 'Quarter',               value: legal.quarter },
        { label: 'Address',               value: legal.address },
        { label: 'IDP Status',            value: legal.isIdp },
        { label: 'IDP Since',             value: legal.idpSince },
      ]} />
      <ReviewSection title="03 — Employment" rows={[
        { label: 'Enrolled in Programme', value: employment.enrolled },
        { label: 'Desired Start Date',    value: employment.startDate },
        ...(employment.jobs || []).flatMap((j, i) => [
          { label: `Position ${i+1} — Company`, value: j.company },
          { label: `Position ${i+1} — Role`,    value: j.role },
          { label: `Position ${i+1} — Years`,   value: j.years },
        ]),
      ]} />
      <ReviewSection title="04 — Education" rows={[
        { label: 'Qualification 1', value: education.edu1Cert },
        { label: 'Institution 1',   value: education.edu1Institution },
        { label: 'Level 1',         value: education.edu1Level },
        { label: 'Year 1',          value: String(education.edu1Year || '') },
        { label: 'Qualification 2', value: education.edu2Cert },
        { label: 'Institution 2',   value: education.edu2Institution },
        { label: 'Level 2',         value: education.edu2Level },
        { label: 'Year 2',          value: String(education.edu2Year || '') },
        ...(education.certifications || []).flatMap((c, i) => [
          { label: `Certification ${i+1}`, value: c.name ? `${c.name} — ${c.institution} (${c.year})` : '' },
        ]),
      ]} />
      <label className="sr-consent">
        <input type="checkbox" checked={consentChecked} onChange={(e) => setConsentChecked(e.target.checked)} />
        <span>
          I confirm that all information provided is accurate and complete. I understand that false or misleading
          statements will result in immediate disqualification and potential legal consequences. I consent to
          Grantsman conducting a due diligence review of my application in accordance with applicable law and
          their privacy policy.
        </span>
      </label>
    </div>
  );
}

function SuccessScreen({ code, onClose }) {
  return (
    <div className="sr-success">
      <div className="sr-success-mark">
        <img src="/mark.PNG" alt="Grantsman" className="sr-success-logo" />
      </div>
      <div className="sr-success-icon">✓</div>
      <h2 className="sr-success-title">Application Submitted</h2>
      <p className="sr-success-body">
        Your SCRIP due diligence application has been received and stored securely. Our team
        will review your submission and contact you within <strong>5–7 business days</strong>.
      </p>
      <div className="sr-code-block">
        <div className="sr-code-label">Your Unique Reference Code</div>
        <div className="sr-code-value">{code}</div>
        <div className="sr-code-note">Save this code — you will need it to track your application status and access your account upon confirmation.</div>
        <button className="sr-copy-btn" onClick={() => navigator.clipboard?.writeText(code)}>📋 Copy Code</button>
      </div>
      <div className="sr-next-steps">
        <div className="sr-next-title">What happens next</div>
        <div className="sr-next-list">
          {[
            ['02', 'Confirmation',                    'Unique code issued, electronic badge generated'],
            ['03', 'Programme Enrolment',              'Formal onboarding into SCRIP'],
            ['04', 'Programme Completion & Workshop',  'With top-tier professionals'],
            ['05', 'Evaluation & Recommendation',      'Due diligence review & professional recommendation'],
          ].map(([num, title, desc]) => (
            <div className="sr-next-item" key={num}>
              <span className="sr-next-num">{num}</span>
              <div><strong>{title}</strong> — {desc}</div>
            </div>
          ))}
        </div>
      </div>
      <button className="btn-primary sr-back-btn" onClick={onClose}>Close ✕</button>
    </div>
  );
}

function validateStep(step, data) {
  const errs = {};
  if (step === 1) {
    if (!data.firstName?.trim())    errs.firstName   = 'Required';
    if (!data.lastName?.trim())     errs.lastName    = 'Required';
    if (!data.dob)                  errs.dob         = 'Required';
    if (!data.gender)               errs.gender      = 'Required';
    if (!data.nationality?.trim())  errs.nationality = 'Required';
    if (!data.idNumber?.trim())     errs.idNumber    = 'Required';
    if (!data.email?.includes('@')) errs.email       = 'Valid email required';
    if (!data.phone?.trim())        errs.phone       = 'Required';
    if (!data.programArea)          errs.programArea = 'Select a track';
  }
  if (step === 2) {
    if (!data.criminalRecord)    errs.criminalRecord = 'Please select Yes or No';
    if (data.criminalRecord === 'Yes' && !data.criminalDetails?.trim())
                                 errs.criminalDetails = 'Please provide details';
    if (!data.country?.trim())   errs.country  = 'Required';
    if (!data.region?.trim())    errs.region   = 'Required';
    if (!data.city?.trim())      errs.city     = 'Required';
    if (!data.address?.trim())   errs.address  = 'Required';
    if (!data.isIdp)             errs.isIdp    = 'Please select Yes or No';
    if (data.isIdp === 'Yes' && !data.idpSince) errs.idpSince = 'Required';
  }
  return errs;
}

export default function ScripRegister({ onClose }) {
  const [step,           setStep]           = useState(1);
  const [errors,         setErrors]         = useState({});
  const [submitting,     setSubmitting]     = useState(false);
  const [submitError,    setSubmitError]    = useState('');
  const [submitted,      setSubmitted]      = useState(false);
  const [refCode,        setRefCode]        = useState('');
  const [consentChecked, setConsentChecked] = useState(false);

  const [identity,   setIdentity]   = useState({});
  const [legal,      setLegal]      = useState({});
  const [employment, setEmployment] = useState({ jobs: [{ company: '', role: '', years: '' }] });
  const [education,  setEducation]  = useState({ certifications: [{ name: '', institution: '', year: '' }] });

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  const stepData = { 1: identity, 2: legal, 3: employment, 4: education };
  const scrollTop = () => document.querySelector('.sr-form-body')?.scrollTo({ top: 0, behavior: 'smooth' });

  const goNext = () => {
    const errs = validateStep(step, stepData[step] || {});
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setErrors({});
    setStep((s) => s + 1);
    scrollTop();
  };

  const goBack = () => { setErrors({}); setStep((s) => s - 1); scrollTop(); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!consentChecked) { setSubmitError('You must confirm your consent before submitting.'); return; }

    setSubmitting(true);
    setSubmitError('');

    try {
      const payload = {
        identity: {
          firstName:     identity.firstName,
          lastName:      identity.lastName,
          dob:           identity.dob,
          gender:        identity.gender,
          nationality:   identity.nationality,
          idNumber:      identity.idNumber,
          email:         identity.email,
          phone:         identity.phone,
          photoFileName: identity.photoName || '',
          programArea:   identity.programArea,
        },
        legal: {
          criminalRecord:  legal.criminalRecord,
          criminalDetails: legal.criminalDetails || '',
          country:  legal.country,
          region:   legal.region,
          city:     legal.city,
          quarter:  legal.quarter || '',
          address:  legal.address,
          isIdp:    legal.isIdp,
          idpSince: legal.idpSince || '',
        },
        employment: {
          jobs:      employment.jobs?.filter(j => j.company || j.role) || [],
          enrolled:  employment.enrolled  || '',
          startDate: employment.startDate || '',
        },
        education: {
          edu1Cert:        education.edu1Cert        || '',
          edu1Institution: education.edu1Institution || '',
          edu1Year:        education.edu1Year        || '',
          edu1Level:       education.edu1Level       || '',
          edu2Cert:        education.edu2Cert        || '',
          edu2Institution: education.edu2Institution || '',
          edu2Year:        education.edu2Year        || '',
          edu2Level:       education.edu2Level       || '',
          certifications:  education.certifications?.filter(c => c.name) || [],
        },
      };

      const photoFile = identity.photo instanceof File ? identity.photo : null;
      const result    = await submitScripApplication(payload, photoFile);

      setRefCode(result.referenceCode);
      setSubmitted(true);
      scrollTop();
    } catch (err) {
      if (err.code === 'DUPLICATE_APPLICATION') {
        setSubmitError(`An active application already exists for this email or phone. Reference: ${err.raw?.error?.referenceCode || ''}`);
      } else {
        setSubmitError(err.message || 'Submission failed. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const progress = ((step - 1) / (STEPS.length - 1)) * 100;

  return (
    <div className="sr-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="sr-modal">
        <div className="sr-header">
          <div className="sr-header-left">
            <img src="/mark.PNG" alt="Grantsman" className="sr-header-mark" />
            <div>
              <div className="sr-header-title">SCRIP Registration</div>
              <div className="sr-header-sub">Strategic Carrier-Readiness Internal Program — Due Diligence</div>
            </div>
          </div>
          <button className="sr-close" onClick={onClose} aria-label="Close">✕</button>
        </div>

        {!submitted ? (
          <>
            <div className="sr-progress-wrap">
              <div className="sr-progress-bar">
                <div className="sr-progress-fill" style={{ width: `${progress}%` }} />
              </div>
              <div className="sr-steps-row">
                {STEPS.map((s) => (
                  <div key={s.num}
                    className={`sr-step-dot${step === s.num ? ' sr-step-dot--active' : ''}${step > s.num ? ' sr-step-dot--done' : ''}`}>
                    <div className="sr-dot-circle">{step > s.num ? '✓' : s.num}</div>
                    <div className="sr-dot-label">{s.short}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="sr-step-heading">
              <span className="sr-step-num">0{step}</span>
              <div>
                <h3 className="sr-step-title">{STEPS[step - 1].label}</h3>
                <div className="sr-step-sub">Due Diligence — Step {step} of {STEPS.length}</div>
              </div>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="sr-form-body">
                {step === 1 && <StepIdentification data={identity}   setData={setIdentity}   errors={errors} />}
                {step === 2 && <StepLegal          data={legal}      setData={setLegal}      errors={errors} />}
                {step === 3 && <StepEmployment     data={employment} setData={setEmployment} />}
                {step === 4 && <StepEducation      data={education}  setData={setEducation}  />}
                {step === 5 && <StepReview formData={{ identity, legal, employment, education }}
                                consentChecked={consentChecked} setConsentChecked={setConsentChecked} />}
              </div>

              {submitError && (
                <div className="sr-submit-error"><span>⚠</span> {submitError}</div>
              )}

              <div className="sr-nav">
                <div className="sr-nav-left">
                  {step > 1 && (
                    <button type="button" className="sr-btn-back" onClick={goBack} disabled={submitting}>← Back</button>
                  )}
                </div>
                <div className="sr-nav-right">
                  {step < STEPS.length ? (
                    <button type="button" className="btn-primary" onClick={goNext}>Continue →</button>
                  ) : (
                    <button type="submit"
                      className={`btn-primary sr-btn-submit${submitting ? ' sr-btn-loading' : ''}`}
                      disabled={submitting}>
                      {submitting
                        ? <span className="sr-spinner-wrap"><span className="sr-spinner" /> Submitting…</span>
                        : 'Submit Application →'}
                    </button>
                  )}
                </div>
              </div>
            </form>
          </>
        ) : (
          <div className="sr-form-body">
            <SuccessScreen code={refCode} onClose={onClose} />
          </div>
        )}
      </div>
    </div>
  );
}